import { CheckIcon, AddIcon, RemoveIcon } from "../components/icons";
import toPriceDisplay from "../utils/toPriceDisplay";
import addLineBreaks from "../utils/addLineBreaks";
import useQuestionState from "../hooks/useQuestionState";
import { questionHelpText } from "../utils/questions";

/**
 * Uma pergunta do produto no wizard do totem, do tablet e do POS: cabeçalho
 * com o que falta escolher, opções em cartões (duas colunas em tela grande) e
 * os botões do passo (`actions`) presos embaixo.
 */
export default function QuestionsCardTotem({
  isAvailableToOrder,
  question,
  orderUuid,
  stepLabel,
  actions,
}) {
  const { description, minQtt, maxQtt } = question;
  const { qtt, isSingleChoice, isCombo, isIncrementDisabled, statefulOptions } =
    useQuestionState(question, orderUuid);

  return (
    <div className="totem-question">
      <div className="totem-question__scroll">
        <header className="totem-question__header">
          {stepLabel && <p className="totem-question__step">{stepLabel}</p>}
          <h2 className="totem-question__title">{description}</h2>
          <div className="totem-question__meta">
            <span className="totem-question__help">
              {questionHelpText(question, { short: true })}
            </span>
            {!isCombo && (
              <QuestionStatus minQtt={minQtt} maxQtt={maxQtt} qtt={qtt} />
            )}
          </div>
        </header>
        <ul className="totem-options">
          {statefulOptions.map((option) => (
            <li key={option.id} className="totem-options__cell">
              <Option
                isAllowedOptionRepetition={question.isAllowedOptionRepetition}
                isAvailableToOrder={isAvailableToOrder && !option.comboItem}
                isSingleChoice={isSingleChoice}
                isIncrementDisabled={isIncrementDisabled}
                option={option}
              />
            </li>
          ))}
        </ul>
      </div>
      <footer className="totem-question__footer">{actions}</footer>
    </div>
  );
}

// "Obrigatório" em cor neutra até ser atendida; depois, o check verde.
function QuestionStatus({ minQtt, maxQtt, qtt }) {
  const isRequired = minQtt > 0;
  const isDone = isRequired && qtt >= minQtt;

  return (
    <span className="totem-question__status">
      {maxQtt > 1 && (
        <span className="totem-chip" aria-label={`${qtt} de ${maxQtt}`}>
          {qtt}/{maxQtt}
        </span>
      )}
      {isRequired && !isDone && (
        <span className="totem-chip totem-chip--required">Obrigatório</span>
      )}
      {isDone && (
        <span className="totem-chip totem-chip--done">
          <CheckIcon /> Pronto
        </span>
      )}
    </span>
  );
}

function Option({
  isAllowedOptionRepetition,
  isAvailableToOrder,
  isSingleChoice,
  isIncrementDisabled,
  option,
}) {
  const isInteractive = isAvailableToOrder && !option.disabled;

  // Um toque no cartão marca/desmarca; com repetição, o primeiro toque põe um
  // e o resto é no −/+.
  const handleClick = !isInteractive
    ? null
    : isAllowedOptionRepetition
      ? option.answeredQtt === 0 && !isIncrementDisabled
        ? option.onIncrementClick
        : null
      : isSingleChoice
        ? option.onRadioClick
        : !isIncrementDisabled || option.isChecked
          ? option.onCheckboxClick
          : null;

  const isSelected = option.answeredQtt > 0;
  const isBlocked =
    isInteractive && !isSelected && !isSingleChoice && isIncrementDisabled;

  const className = [
    "totem-option",
    isSelected ? "is-selected border-color-shop" : "",
    option.disabled || isBlocked ? "is-disabled" : "",
    option.comboItem ? "is-included" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={className}
      role={isInteractive ? "button" : undefined}
      aria-pressed={isInteractive ? isSelected : undefined}
      onClick={handleClick}
    >
      {option.pictureUrl && (
        <img
          src={option.pictureUrl}
          alt=""
          loading="lazy"
          className="totem-option__img"
        />
      )}
      <div className="totem-option__body">
        <span className="totem-option__label">{option.label}</span>
        {option.details && (
          <span className="totem-option__details">
            {addLineBreaks(option.details)}
          </span>
        )}
        {option.disabled && (
          <span className="tag tag--muted totem-option__tag">Indisponível</span>
        )}
        {option.comboItem && (
          <span className="tag tag--muted totem-option__tag">Incluso</span>
        )}
        {!option.disabled && !!parseFloat(option.price) && (
          <span className="totem-option__price">
            + {toPriceDisplay(option.price)}
          </span>
        )}
      </div>
      {isInteractive && isAllowedOptionRepetition && isSelected ? (
        <div className="totem-stepper totem-stepper--compact">
          <button
            type="button"
            className="totem-stepper__button"
            onClick={option.onDecrementClick}
            aria-label={`Tirar um ${option.label}`}
          >
            <RemoveIcon />
          </button>
          <span className="totem-stepper__value">{option.answeredQtt}</span>
          <button
            type="button"
            className="totem-stepper__button"
            onClick={option.onIncrementClick}
            disabled={isIncrementDisabled}
            aria-label={`Adicionar um ${option.label}`}
          >
            <AddIcon />
          </button>
        </div>
      ) : (
        isInteractive &&
        (isAllowedOptionRepetition ? (
          // opção que repete: o primeiro toque põe uma, depois vira o −/+
          <span className="totem-option__check is-round" aria-hidden="true">
            <AddIcon />
          </span>
        ) : (
          <span
            className={`totem-option__check${
              isSingleChoice ? " is-round" : ""
            }${isSelected ? " badge-shop" : ""}`}
            aria-hidden="true"
          >
            {isSelected && <CheckIcon />}
          </span>
        ))
      )}
    </div>
  );
}
